import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiArrowLeft, HiCheck, HiPlus, HiTrash } from "react-icons/hi";
import { PageLayout, ActionButton } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";
import "./CreateShippingCompany.scss";

export const CreateShippingCompany: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['shippingCompanies', 'common']);
  const { token } = useAuth();
  const isRTL = i18n.language === 'ar';
  const [companyName, setCompanyName] = useState("");
  const [maxWeight, setMaxWeight] = useState(0);
  const [maxDiemention, setMaxDiemention] = useState(0);
  const [dwDivisot, setDwDivisot] = useState(0);
  const [insuranceRate, setInsuranceRate] = useState(0);
  const [fuelSurchargeRate, setFuelSurchargeRate] = useState(0);
  const [calculationBase, setCalculationBase] = useState(0); // 0 = PricePerPound, 1 = PricePerTotal
  const [clearance, setClearance] = useState<number | null>(null);
  const [dgFees, setDgFees] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [taxProfileId, setTaxProfileId] = useState<number | null>(null);
  const [taxProfiles, setTaxProfiles] = useState<any[]>([]);
  const [prices, setPrices] = useState([
    {
      key: Date.now(),
      maxWeightRange: "",
      normalFastPrice: "",
      normalEconomyPrice: "",
      dangerousFastPrice: "",
      dangerousEconomyPrice: "",
      sourceCountryId: "",
      destinationCountryId: "",
    },
  ]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await adminApi.call('/country', 'GET');

        if (!response.success) {
          showErrorToast(response.error || t('shippingCompanies:messages.fetchError'));
          return;
        }

        setCountries(response.data?.items);
      } catch (error: any) {
        showErrorToast(t('shippingCompanies:messages.fetchError'));
      }
    };

    fetchCountries();
    adminApi.call<any[]>('/taxprofile', 'GET').then(response => {
      if (response.success) setTaxProfiles(response.data || []);
    });
  }, []);

  // Pre-filter active source and destination countries once using useMemo to avoid performance issues in table rows
  const activeSourceCountries = useMemo(() => {
    return countries?.filter((c: any) => (c.countryType === 0 || c.countryType === 2) && !c.isSuspended) || [];
  }, [countries]);

  const activeDestinationCountries = useMemo(() => {
    return countries?.filter((c: any) => (c.countryType === 0 || c.countryType === 1) && !c.isSuspended) || [];
  }, [countries]);

  const getSourceCountryOptions = (selectedId?: string | number) => {
    const options = [...activeSourceCountries];
    if (selectedId && !options.some((c: any) => c.id == selectedId)) {
      const selected = countries.find((c: any) => c.id == selectedId);
      if (selected) {
        options.push(selected);
      }
    }
    return options.map((c: any) => (
      <option key={c.id} value={c.id}>
        {c.name}
      </option>
    ));
  };

  const getDestinationCountryOptions = (selectedId?: string | number) => {
    const options = [...activeDestinationCountries];
    if (selectedId && !options.some((c: any) => c.id == selectedId)) {
      const selected = countries.find((c: any) => c.id == selectedId);
      if (selected) {
        options.push(selected);
      }
    }
    return options.map((c: any) => (
      <option key={c.id} value={c.id}>
        {c.name}
      </option>
    ));
  };

  const handleInputChange = (index: number, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const updatedPrices = [...prices];
    updatedPrices[index][name] = value;
    setPrices(updatedPrices);
  };

  const handleCompanyFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "companyName") setCompanyName(value);
    else if (name === "maxWeight") setMaxWeight(Number(value));
    else if (name === "maxDiemention") setMaxDiemention(Number(value));
    else if (name === "dwDivisot") setDwDivisot(Number(value));
    else if (name === "insuranceRate") setInsuranceRate(Number(value));
    else if (name === "fuelSurchargeRate") setFuelSurchargeRate(Number(value));
    else if (name === "dgFees") setDgFees(Number(value));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    if (!companyName.trim()) newErrors.companyName = t('shippingCompanies:validation.nameRequired');
    if (!maxWeight || maxWeight <= 0) newErrors.maxWeight = t('shippingCompanies:validation.maxWeightRequired');
    if (!maxDiemention || maxDiemention <= 0) newErrors.maxDiemention = t('shippingCompanies:validation.maxDimensionRequired');
    if (!dwDivisot || dwDivisot <= 0) newErrors.dwDivisot = t('shippingCompanies:validation.dwDivisorRequired');
    if (!taxProfileId) newErrors.taxProfileId = 'Tax profile is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddRow = () => {
    setPrices([
      ...prices,
      {
        key: Date.now(),
        maxWeightRange: "",
        normalFastPrice: "",
        normalEconomyPrice: "",
        dangerousFastPrice: "",
        dangerousEconomyPrice: "",
        sourceCountryId: "",
        destinationCountryId: "",
      },
    ]);
  };

  const deleteRow = (key) => {
    setPrices(prices.filter((item) => item.key !== key));
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showErrorToast(t('shippingCompanies:validation.fillAllFields'));
      return;
    }

    for (let price of prices) {
      if (
        !price.maxWeightRange ||
        !price.normalFastPrice ||
        !price.normalEconomyPrice ||
        !price.dangerousFastPrice ||
        !price.dangerousEconomyPrice ||
        !price.sourceCountryId ||
        !price.destinationCountryId
      ) {
        showErrorToast(t('shippingCompanies:validation.allFieldsRequired'));
        return;
      }
    }

    setLoading(true);

    try {
      const response = await adminApi.call('/shippingcompany', 'POST', {
        name: companyName,
        maxWeight: maxWeight,
        maxDiemention: maxDiemention,
        dwDivisot: dwDivisot,
        insuranceRate: insuranceRate,
        fuelSurchargeRate: fuelSurchargeRate,
        calculationBase: calculationBase,
        clearance: clearance,
        dgFees: dgFees,
        isActive: isActive,
        taxProfileId,
        shippingCompanyPrices: prices.map(({ key, ...p }) => ({
          ...p,
          maxWeightRange: parseFloat(p.maxWeightRange),
          normalFastPrice: parseFloat(p.normalFastPrice),
          normalEconomyPrice: parseFloat(p.normalEconomyPrice),
          dangerousFastPrice: parseFloat(p.dangerousFastPrice),
          dangerousEconomyPrice: parseFloat(p.dangerousEconomyPrice),
          sourceCountryId: parseInt(p.sourceCountryId),
          destinationCountryId: parseInt(p.destinationCountryId),
        })),
      });

      if (!response.success) {
        showErrorToast(response.error || t('shippingCompanies:messages.createError'));
        return;
      }

      showSuccessToast(t('shippingCompanies:messages.createSuccess'));
      setCompanyName("");
      setMaxWeight(0);
      setMaxDiemention(0);
      setDwDivisot(0);
      setInsuranceRate(0);
      setFuelSurchargeRate(0);
      setCalculationBase(0);
      setClearance(null);
      setDgFees(0);
      setPrices([
        {
          key: Date.now(),
          maxWeightRange: "",
          normalFastPrice: "",
          normalEconomyPrice: "",
          dangerousFastPrice: "",
          dangerousEconomyPrice: "",
          sourceCountryId: "",
          destinationCountryId: "",
        },
      ]);
    } catch (error: any) {
      showErrorToast(t('shippingCompanies:messages.createError') + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbs = [
    { label: t('shippingCompanies:breadcrumbs.shippingCompanies'), path: '/shipping-companies' },
    { label: t('shippingCompanies:breadcrumbs.create') }
  ];

  return (
    <PageLayout
      title={t('shippingCompanies:createShippingCompany')}
      subtitle={t('shippingCompanies:subtitle')}
      breadcrumbs={breadcrumbs}
      className={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="modern-form">
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">{t('shippingCompanies:fields.name')} <span className="required">*</span></label>
            <input
              type="text"
              className={`form-control ${errors.companyName ? 'is-invalid' : ''}`}
              name="companyName"
              value={companyName}
              onChange={handleCompanyFieldChange}
              placeholder={t('shippingCompanies:placeholders.name')}
              required
            />
            {errors.companyName && <div className="invalid-feedback">{errors.companyName}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">{t('shippingCompanies:fields.maxWeight')} <span className="required">*</span></label>
            <input
              type="number"
              className={`form-control ${errors.maxWeight ? 'is-invalid' : ''}`}
              name="maxWeight"
              value={maxWeight}
              onChange={handleCompanyFieldChange}
              placeholder={t('shippingCompanies:placeholders.maxWeight')}
              min="0"
              step="0.01"
              required
            />
            {errors.maxWeight && <div className="invalid-feedback">{errors.maxWeight}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">{t('shippingCompanies:fields.maxDimension')} <span className="required">*</span></label>
            <input
              type="number"
              className={`form-control ${errors.maxDiemention ? 'is-invalid' : ''}`}
              name="maxDiemention"
              value={maxDiemention}
              onChange={handleCompanyFieldChange}
              placeholder={t('shippingCompanies:placeholders.maxDimension')}
              min="0"
              step="0.01"
              required
            />
            {errors.maxDiemention && <div className="invalid-feedback">{errors.maxDiemention}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">{t('shippingCompanies:fields.fuelSurchargeRate')}</label>
            <input
              step={0.01}
              type="number"
              className="form-control"
              value={fuelSurchargeRate}
              onChange={(e) => setFuelSurchargeRate(Number(e.target.value))}
              placeholder={t('shippingCompanies:placeholders.fuelSurchargeRate')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Calculation Base</label>
            <select
              className="form-select"
              value={calculationBase}
              onChange={(e) => setCalculationBase(Number(e.target.value))}
            >
              <option value={0}>Price Per Pound (× weight)</option>
              <option value={1}>Price Per Total (flat price)</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Tax Profile</label>
            <select className={`form-select ${errors.taxProfileId ? 'is-invalid' : ''}`} value={taxProfileId ?? ''} onChange={(e) => { setTaxProfileId(e.target.value ? Number(e.target.value) : null); setErrors(prev => ({ ...prev, taxProfileId: '' })); }} required>
              <option value="">Select a tax profile</option>
              {taxProfiles.map(profile => <option key={profile.id} value={profile.id}>{profile.name}</option>)}
            </select>
            {errors.taxProfileId && <div className="invalid-feedback">{errors.taxProfileId}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Clearance Fee ($)</label>
            <input
              step={0.01}
              type="number"
              className="form-control"
              value={clearance ?? ''}
              onChange={(e) => setClearance(e.target.value === '' ? null : Number(e.target.value))}
              placeholder="Enter clearance fee (optional)"
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('shippingCompanies:fields.dwDivisor')} <span className="required">*</span></label>
            <input
              type="number"
              className={`form-control ${errors.dwDivisot ? 'is-invalid' : ''}`}
              name="dwDivisot"
              value={dwDivisot}
              onChange={handleCompanyFieldChange}
              placeholder={t('shippingCompanies:placeholders.dwDivisor')}
              min="0"
              step="0.01"
              required
            />
            {errors.dwDivisot && <div className="invalid-feedback">{errors.dwDivisot}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">{t('shippingCompanies:fields.insuranceRate')}</label>
            <input
              type="number"
              className="form-control"
              value={insuranceRate}
              onChange={(e) => setInsuranceRate(Number(e.target.value))}
              placeholder={t('shippingCompanies:placeholders.insuranceRate')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('shippingCompanies:fields.dgFees')}</label>
            <input
              type="number"
              className="form-control"
              value={dgFees}
              onChange={(e) => setDgFees(Number(e.target.value))}
              placeholder={t('shippingCompanies:placeholders.dgFees')}
            />
          </div>

          <div className="form-group checkbox-group">
            <label className="form-label d-flex align-items-center gap-2">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="form-check-input"
              />
              {t('common:active')}
            </label>
          </div>
        </div>

        <div className="pricing-section">
          <div className="section-header">
            <h5>{t('shippingCompanies:fields.pricingTable')}</h5>
            <ActionButton
              variant="success"
              icon={HiPlus}
              onClick={handleAddRow}
              size="sm"
            >
              {t('shippingCompanies:buttons.addPriceEntry')}
            </ActionButton>
          </div>

          <div className="pricing-table">
            <table className="table table-bordered table-striped">
              <thead className="table-dark">
                <tr>
                  <th>{t('shippingCompanies:table.maxWeight')}</th>
                  <th>{t('shippingCompanies:table.normalFast')}</th>
                  <th>{t('shippingCompanies:table.normalEconomy')}</th>
                  <th>{t('shippingCompanies:table.dangerousFast')}</th>
                  <th>{t('shippingCompanies:table.dangerousEconomy')}</th>
                  <th>{t('shippingCompanies:table.sourceCountry')}</th>
                  <th>{t('shippingCompanies:table.destinationCountry')}</th>
                  <th>{t('shippingCompanies:table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {prices.map((item, index) => (
                  <tr key={item.key}>
                    {[
                      "maxWeightRange",
                      "normalFastPrice",
                      "normalEconomyPrice",
                      "dangerousFastPrice",
                      "dangerousEconomyPrice",
                    ].map((field) => (
                      <td key={field}>
                        <input
                          step={0.01}
                          type="number"
                          name={field}
                          className="form-control"
                          value={item[field]}
                          onChange={(e) => handleInputChange(index, e)}
                        />
                      </td>
                    ))}
                    <td>
                      <select
                        className="form-control"
                        name="sourceCountryId"
                        value={item.sourceCountryId}
                        onChange={(e) => handleInputChange(index, e)}
                      >
                        <option value="">{t('common:select')}</option>
                        {getSourceCountryOptions(item.sourceCountryId)}
                      </select>
                    </td>
                    <td>
                      <select
                        className="form-control"
                        name="destinationCountryId"
                        value={item.destinationCountryId}
                        onChange={(e) => handleInputChange(index, e)}
                      >
                        <option value="">{t('common:select')}</option>
                        {getDestinationCountryOptions(item.destinationCountryId)}
                      </select>
                    </td>
                    <td>
                      <ActionButton
                        variant="danger"
                        size="sm"
                        icon={HiTrash}
                        onClick={() => deleteRow(item.key)}
                        tooltip={t('common:delete')}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="form-actions">
          <ActionButton
            variant="primary"
            icon={HiCheck}
            onClick={handleSubmit}
            loading={loading}
            disabled={loading}
          >
            {loading ? t('shippingCompanies:buttons.submitting') : t('shippingCompanies:buttons.submit')}
          </ActionButton>
        </div>
      </div>
    </PageLayout>
  );
};
